import { Camera, Pencil, PenLine, Plus } from 'lucide-react';

export const getInitialValues = (attributes: any) => ({
  active: true,
  policy_holder: attributes?.policy_holder || 'myself',
  insurance_company_id: attributes?.insurance_company?.data?.id || '',
  plan_type: attributes?.plan_type || '',
  member_id: attributes?.member_id || '',
  group_number: attributes?.group_number || '',
  card_front_pic: attributes?.card_front_pic || '',
  card_back_pic: attributes?.card_back_pic || ''
})

export const POLICY_HOLDER_LABELS = {
  myself: 'Myself',
  spouse_partner: 'Spouse / Partner',
  parent_guardian: 'Parent / Guardian',
  someone_else: 'Someone else'
}

export const PLAN_TYPE_LABELS = {
  ppo: 'PPO',
  hmo: 'HMO',
  dhmo: 'DHMO',
  epo: 'EPO',
  indemnity: 'Indemnity',
  discount: 'Discount'
}

export const insurancePlanTypes = Object.entries(PLAN_TYPE_LABELS).map(([value, label]) => ({ value, label }));

export const OWN_PLAN = 'myself';

export const otherPolicyHolders = Object.entries(POLICY_HOLDER_LABELS)
  .filter(([title]) => title !== OWN_PLAN)
  .map(([title, label]) => ({ title, label }));

export const PLAN_OWNERSHIP = [
  { own: true, label: "Yes, it's mine" },
  { own: false, label: "No, someone else's" },
];

export const getCarrierOptions = (practice: any) =>
  (practice?.insurance_companies?.data ?? []).map((company) => ({
    value: company.id ?? '',
    label: company.attributes?.title ?? '',
    logo: company.attributes?.logo_url,
  }));

export const getIntroCopy = (attributes: any) => {
  if (attributes?.active === false)
    return {
      icon: Plus,
      title: "You're marked as self-pay",
      description: "Got dental coverage now? Add it and we'll check your benefits before your visit.",
      action: 'Add insurance',
    };

  if (!attributes?.active)
    return {
      icon: Plus,
      title: 'Nothing on file yet',
      description: "If you have dental coverage, add it and we'll check your benefits before your visit. Takes about a minute.",
      action: 'Add insurance',
    };

  return {
    icon: Pencil,
    title: 'Update your insurance',
    description: 'Change what we have on file, or switch to self-pay if your coverage has changed.',
    action: 'Update insurance',
  };
};

export const DEFAULT_FLOW = 'typed';

const FULL_FLOW = 'both';

export const INSURANCE_FLOWS = {
  photo: ['method', 'card', 'holder', 'review'],
  typed: ['method', 'details', 'holder', 'review'],
  [FULL_FLOW]: ['method', 'card', 'details', 'holder', 'review'],
};

// Finds a step in the current flow; a step from the other path switches to the combined flow so every step stays in sequence.
export const locateStep = (stepId: string, currentFlow: string) => {
  const flow = INSURANCE_FLOWS[currentFlow].includes(stepId) ? currentFlow : FULL_FLOW;

  return { flow, index: INSURANCE_FLOWS[flow].indexOf(stepId) };
};

const cardPhotoSummary = ({ card_front_pic, card_back_pic }) => {
  if (card_front_pic && card_back_pic) return 'Front & back added';
  if (card_front_pic) return 'Front added';
  if (card_back_pic) return 'Back added';
  return '';
};

export const getReviewRows = (values: any, carrierOptions: any[]) => {
  const carrier = carrierOptions.find(({ value }) => value === values.insurance_company_id)?.label;

  return [
    { label: 'Carrier', value: [carrier, PLAN_TYPE_LABELS[values.plan_type]].filter(Boolean).join(' · '), step: 'details' },
    { label: 'Member ID', value: values.member_id, step: 'details' },
    { label: 'Group number', value: values.group_number, step: 'details' },
    { label: 'Policy holder', value: POLICY_HOLDER_LABELS[values.policy_holder], step: 'holder' },
    { label: 'Card photo', value: cardPhotoSummary(values), placeholder: 'Not added', step: 'card' },
  ];
};

export const ADD_METHODS = [
  {
    flow: 'photo',
    icon: Camera,
    title: 'Take a photo of my card',
    description: "We'll read the details off it — fastest way",
    badge: 'Fastest',
  },
  {
    flow: 'typed',
    icon: PenLine,
    title: "I'll type it in",
    description: 'Carrier, member ID and group number',
  },
];

export const BACK_BTN = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-secondary-700 transition-colors hover:bg-secondary-50 disabled:opacity-60';
export const PRIMARY_BTN ='inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[10px] bg-teamcare-blue px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-teamcare-blue-dark disabled:opacity-60 sm:px-5 sm:py-[11px] sm:text-sm';
export const SECONDARY_BTN = 'inline-flex items-center justify-center whitespace-nowrap rounded-[10px] border-[1.5px] border-secondary-200 bg-white px-4 py-2.5 text-[13px] font-semibold text-secondary-600 transition-colors hover:border-secondary-300 hover:bg-secondary-50 disabled:opacity-60 sm:px-5 sm:py-[11px] sm:text-sm';

const typedInFields = ['policy_holder', 'insurance_company_id', 'plan_type', 'member_id', 'group_number'];
const cardPicFields = ['card_front_pic', 'card_back_pic'];

export const formatPayload = (values: any) => {
  const formData = new FormData();

  formData.append('active', String(values.active));
  if (!values.active) return formData;

  typedInFields.forEach((key) => formData.append(key, values[key]));

  cardPicFields.forEach((key) => {
    if (values[key] instanceof File)
      formData.append(key, values[key]);
  });

  return formData;
};
